import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { slide as BurgerMenu } from 'react-burger-menu';
// 1. Swap Polyglot for i18next
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { Button } from './buttons';
import Modal from './Modal';
import Logo from './Logo';
import { Help, Delete, Plus } from './icons';
import HeaderDropdown from './HeaderDropdown';
import QuestionCreateContainer from '../containers/QuestionCreateContainer';
import './AskListHeaderMenu.scss';

// Sub-component refactored to use the hook
function LogInMessage() {
  const { t } = useTranslation();
  
  /**
   * Note: In react-polyglot, variables were %{var}. 
   * In react-i18next, they are usually {{var}}.
   * I've updated the regex to match the standard i18next format.
   */
  const logInMessageTranslation = t('askList.logInMessage');
  const logInMessageParts = logInMessageTranslation.split(/{{logInLink}}/);

  return (
    <>
      {logInMessageParts[0]}
      <NavLink className="text-link" to="/questions">
        {t('askList.logIn')}
      </NavLink>
      {logInMessageParts.length > 1 && logInMessageParts[1]}
    </>
  );
}

const AskListHeaderMenu = ({ 
  fetchAskList, 
  profile = null 
}) => {
  // 2. Initialize translation hook
  const { t } = useTranslation();
  
  const [burgerMenuOpen, setBurgerMenuOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const openModal = (e) => {
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
    setBurgerMenuOpen(false);
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);
  const closeBurgerMenu = () => setBurgerMenuOpen(false);

  const renderAskList = () => (
    <div className="ask-list-header-menu__contents">
      {profile ? (
        <div>
          <Button
            className="ask-list-header-menu__add-button"
            onClick={openModal}
            colorScheme="blue"
          >
            <div className="header-menu-dropdown__link-contents">
              <Plus
                className="header-button__account-icon header-button__account-icon--logout"
                width="20"
                height="20"
                viewBox="0 0 48 48"
                fill="white"
              />
              <span>{t('askList.addAQuestion')}</span>
            </div>
          </Button>
          <NavLink
            onClick={closeBurgerMenu}
            className="full-width-link header-menu-dropdown__link"
            to="/questions"
          >
            <span>{t('askList.seeAllQuestions')}</span>
          </NavLink>
        </div>
      ) : (
        <div className="ask-list-header-menu__no-questions">
          <Help />
          <div>
            <LogInMessage />
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="ask-list-header-menu__container">
      <HeaderDropdown
        className="ask-list-header-menu__dropdown"
        onOpen={fetchAskList}
        displayArrow={false}
        containerWidth="280px"
        title={
          <Help
            width="32"
            height="32"
            viewBox="0 0 32 32"
            label={t('nav.askList')}
          />
        }
        align="right"
      >
        {renderAskList()}
      </HeaderDropdown>
      
      <BurgerMenu
        tabIndex="-1"
        isOpen={burgerMenuOpen}
        burgerButtonClassName="ask-list-header-menu__burger-menu"
        right
        width="87.5%"
        menuClassName="burger-menu burger-menu--right"
        overlayClassName="burger-menu-overlay"
        customCrossIcon={<Delete label="close" />}
        customBurgerIcon={<Help label={t('nav.askList')} />}
        onStateChange={(state) => {
          setBurgerMenuOpen(state.isOpen);
          if (state.isOpen) {
            fetchAskList();
          }
        }}
      >
        <div className="header__menu-header">
          <Logo className="header__menu-logo" type="icon" />
          <h4>{t('nav.myAskList')}</h4>
        </div>
        {renderAskList()}
      </BurgerMenu>

      {profile && (
        <Modal isOpen={modalOpen} onRequestClose={closeModal}>
          <QuestionCreateContainer onQuestionAdded={closeModal} />
        </Modal>
      )}
    </div>
  );
};

AskListHeaderMenu.propTypes = {
  fetchAskList: PropTypes.func.isRequired,
  profile: PropTypes.shape({
    id: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string
  })
};

// 3. Remove HOC export
export default AskListHeaderMenu;
